import { useState, useCallback } from 'react';
import type { IComment, ICreateCommentPayload, IUpdateCommentPayload, CommentableType } from '@/lib/types/comment';
import { commentService } from '@/lib/services/comment';
import { toast } from 'sonner';

interface UseCommentsProps {
  commentableType: CommentableType;
  commentableId: number;
  token?: string;
}

export const useComments = ({ 
  commentableType, 
  commentableId, 
  token 
}: UseCommentsProps) => {
  const [comments, setComments] = useState<IComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);

  const fetchComments = useCallback(async (page = 1) => {
    try {
      setIsLoading(true);
      const response = await commentService.getComments(
        commentableType, 
        commentableId,
        { page },
        { token }
      );
      if (page === 1) {
        setComments(response.data);
      } else {
        setComments(prev => [...prev, ...response.data]);
      }
      setHasNextPage(!!response.paginate.next);
      setCurrentPage(response.paginate.current_page);
    } catch (error) {
      toast.error('Failed to fetch comments');
    } finally {
      setIsLoading(false);
    }
  }, [commentableType, commentableId, token]);

  const loadMore = async () => {
    if (!hasNextPage || isLoading) return;
    await fetchComments(currentPage + 1);
  };

  const addComment = async (payload: Omit<ICreateCommentPayload, 'commentable_type' | 'commentable_id'>) => {
    try {
      await commentService.createComment({
        ...payload,
        commentable_type: commentableType,
        commentable_id: commentableId,
      }, { token });
      
      await fetchComments(1);
      toast.success('Comment added successfully');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const updateComment = async (id: number, payload: IUpdateCommentPayload) => {
    try {
      await commentService.updateComment(id, payload, { token });
      await fetchComments(1);
      toast.success('Comment updated successfully');
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  const deleteComment = async (id: number) => {
    try {
      await commentService.deleteComment(id, { token });
      await fetchComments(1);
      toast.success('Comment deleted successfully');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  return {
    comments,
    isLoading,
    hasNextPage,
    fetchComments,
    loadMore,
    addComment,
    updateComment,
    deleteComment,
  };
}; 